import type { LucideIcon } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { kpiIconClasses } from '../lib/reporteColor'

/** Insignia de variación porcentual (ej. Día/Semana/Mes vs. período anterior). */
export interface KpiBadge {
  label: string
  /** Variación en %. `null` = sin base de comparación (se muestra "—"). */
  pct: number | null
}

interface KpiCardProps {
  icon: LucideIcon
  label: string
  value: string | number
  hint?: string
  /** Nombre del color Tailwind (ej. 'emerald', 'blue'). Default: 'emerald'. */
  colorNombre?: string
  /** Tono del color (ej. '500'). Default: '500'. */
  colorTono?: string
  /** Si true, agrega gradiente sutil al fondo del card (estilo medifarma). */
  gradient?: boolean
  /** Insignias de variación mostradas bajo el hint (ej. Día/Semana/Mes). */
  badges?: KpiBadge[]
}

function BadgeVariacion({ label, pct }: KpiBadge) {
  const up = pct != null && pct > 0
  const down = pct != null && pct < 0
  const cls =
    pct == null
      ? 'text-muted-foreground bg-muted'
      : up
        ? 'text-green-700 bg-green-500/10 dark:text-green-400'
        : down
          ? 'text-pink-700 bg-pink-500/10 dark:text-pink-400'
          : 'text-muted-foreground bg-muted'
  const flecha = pct == null ? '' : up ? '▲' : down ? '▼' : ''
  const texto = pct == null ? '—' : `${up ? '+' : ''}${pct.toFixed(1)}%`
  return (
    <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-medium ${cls}`}>
      <span className="text-muted-foreground">{label}</span> {flecha}
      {texto}
    </span>
  )
}

export function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  colorNombre = 'emerald',
  colorTono = '500',
  gradient,
  badges,
}: KpiCardProps) {
  const c = kpiIconClasses(colorNombre, colorTono)
  const gradientClass = gradient
    ? `bg-gradient-to-r from-${colorNombre}-500/15 via-${colorNombre}-500/5 via-50% to-transparent dark:from-${colorNombre}-800/20 dark:via-${colorNombre}-900/10 dark:via-50% dark:to-transparent`
    : ''
  return (
    <Card className={`border border-gray-300 dark:border-gray-700 ${gradientClass}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`flex h-10 w-10 items-center justify-center rounded-md ${c.wrap}`}>
            <Icon className={`h-5 w-5 ${c.icon}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="mt-1 text-2xl font-semibold text-black dark:text-white truncate">{value}</p>
            {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
            {badges && badges.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {badges.map((b) => (
                  <BadgeVariacion key={b.label} label={b.label} pct={b.pct} />
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
